import * as React from "react";
import { Download, Lock, RefreshCw, Trash2, ShieldCheck, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail, BACKEND } from "@/lib/api";
import DetailDrawer from "@/components/DetailDrawer";
import { fmtCents } from "@/lib/time";
import { toast } from "sonner";

export default function RunDetailDrawer({ open, onOpenChange, run, companyId, canApprove, onChanged }) {
  const [lines, setLines] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open || !run) return;
    setLines(null);
    api.get(`/companies/${companyId}/pay/runs/${run.id}/lines`)
      .then(({data}) => setLines(data.lines))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)));
  }, [open, run, companyId]);

  if (!run) return <DetailDrawer open={open} onOpenChange={onOpenChange} title="Pay run" testid="pay-run-drawer" />;

  const isDraft = run.status === "draft";

  async function regen() {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/pay/runs/${run.id}/regenerate`);
      toast.success("Regenerated");
      onChanged && onChanged();
      // refresh lines
      const { data } = await api.get(`/companies/${companyId}/pay/runs/${run.id}/lines`);
      setLines(data.lines);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }
  async function approve() {
    if (!window.confirm(`Approve this run? Gross ${fmtCents(run.totals.gross_cents)} will be locked.`)) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/pay/runs/${run.id}/approve`);
      toast.success("Run approved and locked");
      onChanged && onChanged();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete this draft run?")) return;
    setBusy(true);
    try {
      await api.delete(`/companies/${companyId}/pay/runs/${run.id}`);
      toast.success("Removed");
      onChanged && onChanged();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <DetailDrawer open={open} onOpenChange={onOpenChange}
                  title={`${run.period_start} → ${run.period_end}`}
                  subtitle={`${run.period_type} · ${run.status}`}
                  testid="pay-run-drawer">
      <div className="p-5 space-y-4">
        {/* Totals strip */}
        <div className="grid grid-cols-4 gap-2">
          <MiniStat label="Lines" v={run.totals.line_count}/>
          <MiniStat label="Reg h" v={run.totals.hours_reg.toFixed(1)}/>
          <MiniStat label="OT h" v={run.totals.hours_ot.toFixed(1)}/>
          <MiniStat label="Gross" v={fmtCents(run.totals.gross_cents)} big/>
        </div>

        {run.totals.missing_rate_count > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2"
               data-testid="pay-run-missing-rate">
            <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5"/>
            <div className="text-[12px] text-amber-800">
              <span className="mono-num font-semibold">{run.totals.missing_rate_count}</span> line(s) have no HR
              hourly rate — those staff are excluded from totals. Set a rate in HR and hit
              <span className="mono-num"> Regenerate</span>.
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {isDraft && (
            <button className="btn-outline" onClick={regen} disabled={busy} data-testid="pay-run-regenerate">
              <RefreshCw size={13}/> Regenerate
            </button>
          )}
          <a href={`${BACKEND}/api/companies/${companyId}/pay/runs/${run.id}/csv`}
             target="_blank" rel="noreferrer" className="btn-outline"
             data-testid="pay-run-csv">
            <Download size={13}/> Export CSV
          </a>
          {isDraft && canApprove && (
            <button className="btn-violet ml-auto" onClick={approve} disabled={busy}
                    data-testid="pay-run-approve">
              <ShieldCheck size={13}/> Approve & lock
            </button>
          )}
          {isDraft && (
            <button className="btn-outline text-brand-coral border-brand-coral/40"
                    onClick={del} disabled={busy}
                    data-testid="pay-run-delete">
              <Trash2 size={13}/> Delete
            </button>
          )}
          {!isDraft && (
            <div className="ml-auto chip chip-emerald mono-num text-[10px]">
              <Lock size={10}/> Immutable
            </div>
          )}
        </div>

        {/* Lines table */}
        <div>
          <div className="micro-label mb-1">Lines</div>
          {!lines ? (
            <div className="text-[12px] text-brand-muted py-4">Loading…</div>
          ) : (
            <div className="card-panel overflow-hidden">
              <div className="table-scroll">
                <table className="fable-table" data-testid="pay-run-lines-table">
                  <thead>
                    <tr>
                      <th style={{width:"30%"}}>Member</th>
                      <th className="text-right" style={{width:"14%"}}>Reg h</th>
                      <th className="text-right" style={{width:"12%"}}>OT h</th>
                      <th className="text-right" style={{width:"14%"}}>Rate</th>
                      <th className="text-right">Gross</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => (
                      <tr key={l.id} data-testid={`pay-run-line-${l.user_id}`}>
                        <td className="text-[12.5px] text-brand-ink">
                          <div className="flex items-center gap-2">
                            <span className="h-5 w-5 rounded-full bg-brand-violet text-white text-[9px] font-bold flex items-center justify-center">
                              {(l.user_name || "?").charAt(0).toUpperCase()}
                            </span>
                            <span>{l.user_name}</span>
                            {l.missing_rate && (
                              <span className="chip chip-amber mono-num text-[10px]">missing_rate</span>
                            )}
                          </div>
                        </td>
                        <td className="mono-num text-right">{l.hours_reg.toFixed(2)}</td>
                        <td className="mono-num text-right text-brand-muted">{l.hours_ot.toFixed(2)}</td>
                        <td className="mono-num text-right text-brand-muted">
                          {l.hourly_rate_cents ? fmtCents(l.hourly_rate_cents) : "—"}
                        </td>
                        <td className="mono-num text-right font-semibold text-brand-ink">
                          {fmtCents(l.gross_cents)}
                        </td>
                      </tr>
                    ))}
                    {lines.length === 0 && (
                      <tr><td colSpan={5} className="text-center py-8 text-[12px] text-brand-muted">
                        No lines. Regenerate after approving time entries in Rotas → Timesheets.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </DetailDrawer>
  );
}

function MiniStat({ label, v, big }) {
  return (
    <div className="rounded-md border border-brand-rule bg-card px-2.5 py-1.5">
      <div className="micro-label">{label}</div>
      <div className={`mt-0.5 mono-num ${big ? 'text-[15px] font-bold text-brand-violet' : 'text-[13px] text-brand-ink font-semibold'}`}>
        {v}
      </div>
    </div>
  );
}
